// Inspired by https://www.fffuel.co/gggrain/

import { classMerge } from '@components/utilities/classMerge'

interface SvgGradientFilterProps {
  /** CSS tailwind class name */
  className?: string
  /** Controls grain size. Range: 0.1 to 2 (default: 0.65) */
  frequency?: number
  /** Controls grain intensity. Range: 1 to 10 (default: 6) */
  granularity?: number
  /** Filter ID if you need multiple filters */
  id?: string
  /** CSS mix-blend-mode values */
  mixBlendMode?: 'soft-light' | 'normal' | 'overlay' | 'exclusion'
  /** Controls grain opacity. Range: 0 to 1 (default: 0.46) */
  opacity?: number
  /** Random seed for pattern variation */
  seed?: number
}

/**
 * SVG-based grain filter component for adding texture overlays
 *
 * @param frequency - Controls grain size. Range: 0.1 to 2 (default: 0.65)
 * @param granularity - Controls grain intensity. Range: 1 to 10 (default: 6)
 * @param seed - Random seed for pattern variation
 * @param id - Filter ID if you need multiple filters
 * @param opacity - Controls grain opacity. Range: 0 to 1 (default: 0.46)
 * @param mixBlendMode - CSS mix-blend-mode values
 */
export const GrainySvgGradientFilter = ({
  className,
  frequency = 0.65,
  granularity = 6,
  id = 'grain',
  mixBlendMode = 'soft-light',
  opacity = 0.46,
  seed = 2,
}: SvgGradientFilterProps) => {
  // Map granularity from 1-10 scale to the matrix values
  const map = (
    value: number,
    inMin: number,
    inMax: number,
    outMin: number,
    outMax: number,
  ) => {
    return outMin + ((value - inMin) * (outMax - outMin)) / (inMax - inMin)
  }

  const granularity1 = Math.round(map(granularity, 10, 1, 15, 25))
  const granularity2 = Math.round(map(granularity, 10, 1, -7, -17))

  return (
    <>
      {/* Grain overlay */}
      <div
        aria-hidden='true'
        className={classMerge(
          'pointer-events-none absolute inset-0',
          className,
        )}
        style={{
          filter: `url(#${id})`,
          mixBlendMode,
          opacity,
        }}
      />
      <svg height='0' style={{ position: 'absolute' }} width='0'>
        <defs>
          <filter id={id}>
            <feTurbulence
              baseFrequency={frequency}
              numOctaves='2'
              seed={seed}
              stitchTiles='stitch'
              type='fractalNoise'
            />
            <feColorMatrix type='saturate' values='0' />
            <feComponentTransfer>
              <feFuncR slope='3' type='linear' />
              <feFuncG slope='3' type='linear' />
              <feFuncB slope='3' type='linear' />
            </feComponentTransfer>
            <feColorMatrix
              type='matrix'
              values={`1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 ${granularity1} ${granularity2}`}
            />
          </filter>
        </defs>
      </svg>
    </>
  )
}
